import { FC, useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/elements/Modal";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Form } from "@/components/elements/Form";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useShareUrlSchema } from "@/features/case/schemas/shareUrlSchema";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Button } from "@/components/elements/Button";
import { IconActionHTTP, IconContentFileCopy, IconInfoOutline, IconVisibility, IconVisibilityOff } from "@/assets/icons";
import { toast } from "@/utils/toast";
import dayjs from "dayjs";
import { useDialog } from "@/hooks/useDialog";
import { black } from "@/theme/colors";
import { InputAdornment } from "@mui/material";
import { IconBox } from "@/components/elements/IconBox";
import { useMutation } from "@tanstack/react-query";
import { upsertSharingApi } from "@/api/services/main/sharing";
import { SHARE_TYPE, TSharingEntityType, TUpsertSharingReqDto } from "@/api/services/main/sharing/dtos/sharing.dto";
import { showError } from "@/utils/error";
import { API_SUCCESS_MESSAGE, COPY_FAILED_MESSAGE } from "@/constants/common";
import { v4 as uuidv4 } from "uuid";
import { Radio } from "@/components/elements/Radio";

/** Per-type share link data */
type TShareLinkInfo = {
  shareUrl: string | null;
  sharePassword: string | null;
  shareUrlExpiredAt?: string | null;
};

type TShareEnterpriseURLModalProps = {
  modal: UseVisibleReturn;
  /** Modal title */
  title: string;
  /** Modal description below title */
  description: string;
  /** Entity type for sharing API */
  entityType: TSharingEntityType;
  /** Entity ID for sharing API */
  entityId?: string | number;
  /** Default share type: 0 = full, 1 = masked */
  shareType?: number;
  /** Per-type share links (full & masked). When provided, radio switches between them. */
  shareLinks?: { full?: TShareLinkInfo | null; masked?: TShareLinkInfo | null };
  /** Legacy single-link props — used when shareLinks is not provided */
  initialShareUrl?: string;
  initialPassword?: string;
  initialShareUrlExpiredAt?: string;
  /** Called after successfully generating/saving a share link */
  onGenerateSuccess?: () => void;
  /** Show radio buttons to choose between full/masked share type inside the modal */
  showShareTypeSelector?: boolean;
  /** Tooltip text shown next to the masking radio option */
  maskingTooltipText?: string;
};

type TShareForm = {
  link?: string;
  password?: string;
};

/** Generate a random password (32 chars, alphanumeric) */
const generatePassword = (): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((v) => chars[v % chars.length])
    .join("");
};

/** Build full share URL from token for display/copy */
const buildShareUrl = (token: string): string => `${window.location.origin}/shared/${token}`;

/** Resolve initial data for a given share type from shareLinks or legacy props */
const resolveShareData = (
  shareType: number,
  shareLinks?: TShareEnterpriseURLModalProps["shareLinks"],
  initialShareUrl?: string,
  initialPassword?: string,
  initialShareUrlExpiredAt?: string,
) => {
  if (shareLinks) {
    const link = shareType === SHARE_TYPE.FULL ? shareLinks.full : shareLinks.masked;
    return {
      url: link?.shareUrl ?? "",
      password: link?.sharePassword ?? "",
      expiredAt: link?.shareUrlExpiredAt ?? undefined,
    };
  }
  return { url: initialShareUrl ?? "", password: initialPassword ?? "", expiredAt: initialShareUrlExpiredAt };
};

/** Reusable share URL modal — works with any entity type (case, job, talent) */
export const ShareEnterpriseURLModal: FC<TShareEnterpriseURLModalProps> = ({
  modal,
  title,
  description,
  entityType,
  entityId,
  shareType: externalShareType,
  shareLinks,
  initialShareUrl = "",
  initialPassword = "",
  initialShareUrlExpiredAt,
  onGenerateSuccess,
  showShareTypeSelector = false,
  maskingTooltipText,
}) => {
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [shareUrlExpiredAt, setShareUrlExpiredAt] = useState<string | undefined>(initialShareUrlExpiredAt);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [selectedShareType, setSelectedShareType] = useState<number>(externalShareType ?? SHARE_TYPE.FULL);
  const { shareUrlSchema } = useShareUrlSchema();
  const confirmRegenerateDialog = useDialog();

  const activeShareType = showShareTypeSelector ? selectedShareType : externalShareType;

  const updateSharePassword = useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: TUpsertSharingReqDto }) => upsertSharingApi(entityType, id, payload),
    onSuccess: () => {
      toast.success(API_SUCCESS_MESSAGE);
    },
    onError: (error: unknown) => {
      showError(error);
    },
  });

  const methods = useForm<TShareForm>({
    defaultValues: { link: "", password: "" },
    resolver: yupResolver(shareUrlSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, watch, setValue } = methods;

  // Derive current share data from selected type
  const currentShareData = resolveShareData(selectedShareType, shareLinks, initialShareUrl, initialPassword, initialShareUrlExpiredAt);

  /** Sync form fields when share type radio changes or share data changes */
  useEffect(() => {
    const newLink = currentShareData.url ? buildShareUrl(currentShareData.url) : "";
    setValue("link", newLink);
    setValue("password", currentShareData.password);
    setShareUrlExpiredAt(currentShareData.expiredAt ?? undefined);
  }, [selectedShareType, currentShareData.url, currentShareData.password, currentShareData.expiredAt, setValue]);

  useEffect(() => {
    if (externalShareType !== undefined) {
      setSelectedShareType(externalShareType);
    }
  }, [externalShareType]);

  const linkValue = watch("link");
  const passwordValue = watch("password");
  const hasShareUrl = !!linkValue;

  const generateAndSave = useCallback(async () => {
    const token = uuidv4();
    const newPassword = generatePassword();
    const newExpiredAt = dayjs().add(1, "month").toISOString();

    setValue("link", buildShareUrl(token));
    setValue("password", newPassword);
    setShareUrlExpiredAt(newExpiredAt);

    if (entityId) {
      await updateSharePassword.mutateAsync({
        id: entityId,
        payload: { sharePassword: newPassword, shareUrl: token, shareUrlExpiredAt: newExpiredAt, shareType: activeShareType },
      });
      onGenerateSuccess?.();
    }
  }, [entityId, setValue, updateSharePassword, activeShareType, onGenerateSuccess]);

  const isExpired = !!shareUrlExpiredAt && dayjs(shareUrlExpiredAt).isBefore(dayjs());

  const handleGenerateUrl = useCallback(async () => {
    if (hasShareUrl && !isExpired) {
      const confirmed = await confirmRegenerateDialog.show();
      if (!confirmed) return;
    }
    await generateAndSave();
  }, [hasShareUrl, isExpired, confirmRegenerateDialog, generateAndSave]);

  const handleCopy = useCallback(async () => {
    try {
      const lines = [`共有URL：`, `${linkValue}`];
      const trimmedPwd = passwordValue?.trim();
      if (trimmedPwd) {
        lines.push(`閲覧パスワード：`, trimmedPwd);
      }
      await navigator.clipboard.writeText(lines.join("\n"));
      (document.activeElement as HTMLElement)?.blur();
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 2000);
    } catch {
      toast.error(COPY_FAILED_MESSAGE);
    }
  }, [linkValue, passwordValue]);

  return (
    <Modal isOpen={modal.visible} onClose={modal.close} width={800} title={title} enableFooter={false}>
      <Typography variant="cap12">{description}</Typography>
      <Form methods={methods} onSubmit={() => {}} schema={shareUrlSchema}>
        <Stack gap={3} mt={3}>
          {/* Share type radio selector */}
          {showShareTypeSelector && (
            <Stack direction="column" alignItems="start" gap={2}>
              <Stack direction="row">
                <Typography variant="cap12">企業への共有事項</Typography>
                {maskingTooltipText && (
                  <Tooltip
                    title={
                      <Typography variant="body14" sx={{ whiteSpace: "pre-line" }}>
                        {maskingTooltipText}
                      </Typography>
                    }
                    arrow
                    placement="bottom"
                  >
                    <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <IconInfoOutline width={16} height={16} />
                    </Box>
                  </Tooltip>
                )}
              </Stack>

              <Stack direction="row" alignItems="center" gap={2} mt={-2}>
                <Radio
                  label="全ての人材情報"
                  value={SHARE_TYPE.FULL}
                  checked={selectedShareType === SHARE_TYPE.FULL}
                  onChange={() => setSelectedShareType(SHARE_TYPE.FULL)}
                  nonControl
                />
                <Radio
                  label="一部マスキングした人材情報"
                  value={SHARE_TYPE.MASKED}
                  checked={selectedShareType === SHARE_TYPE.MASKED}
                  onChange={() => setSelectedShareType(SHARE_TYPE.MASKED)}
                  nonControl
                />
              </Stack>
            </Stack>
          )}

          {/* URL field + generate button */}
          <Stack direction="row" alignItems="end" spacing={3} justifyContent="space-between">
            <Stack flex={1}>
              <FormField control={control} name="link" label="共有URL">
                <Input placeholder="" disabled sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: black[30], color: black[30] } }} />
              </FormField>
            </Stack>
            <Button
              variant="secondary"
              startIcon={<IconActionHTTP />}
              onClick={handleGenerateUrl}
              isLoading={updateSharePassword.isPending}
              size="sm"
              sx={{ width: "116px" }}
            >
              {hasShareUrl ? "URL再発行" : "URL発行"}
            </Button>
          </Stack>
          {hasShareUrl && (
            <Typography variant="cap12" color={black[30]} mt={-2}>
              有効期限：{dayjs(shareUrlExpiredAt).format("YYYY/MM/DD")}まで
            </Typography>
          )}

          {/* Password field — editable */}
          <FormField control={control} name="password" label="閲覧パスワード">
            <Input
              placeholder=""
              type={!passwordValue || passwordVisible ? "text" : "password"}
              disabled
              sx={{ "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: black[30], color: black[30] } }}
              endAdornment={
                <InputAdornment position="end" sx={{ opacity: 1, pointerEvents: "auto" }}>
                  <IconBox onClick={() => setPasswordVisible((v) => !v)} sx={{ p: 0, cursor: "pointer" }}>
                    {passwordVisible ? <IconVisibilityOff width={15} height={15} /> : <IconVisibility width={15} height={15} />}
                  </IconBox>
                </InputAdornment>
              }
            />
          </FormField>
        </Stack>
      </Form>
      <Stack direction="row" mt={3} alignItems="center" justifyContent="space-between">
        <Button variant="ghost" onClick={modal.close} size="sm" sx={{ width: "102px" }}>
          キャンセル
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title="コピーしました" open={copyTooltipOpen} arrow placement="top">
            <span>
              <Button
                variant="secondary"
                size="sm"
                sx={{ width: "172px" }}
                startIcon={<IconContentFileCopy />}
                onClick={handleCopy}
                disabled={!hasShareUrl}
              >
                共有情報をコピー
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {isExpired && (
        <Stack direction="row" justifyContent="flex-end">
          <Typography variant="cap12" sx={{ color: "#EC7126", mt: 1 }}>
            共有URLの有効期限が切れています。URL再発行してください。
          </Typography>
        </Stack>
      )}

      {/* Confirmation dialog for URL regeneration */}
      <Modal
        isOpen={confirmRegenerateDialog.open}
        onClose={confirmRegenerateDialog.cancel}
        onOk={confirmRegenerateDialog.confirm}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        title="URL再発行の確認"
      >
        <Stack mb={2}>
          <Typography>すでに発行されたURLは無効になりますがよろしいですか？</Typography>
        </Stack>
      </Modal>
    </Modal>
  );
};
